import { Botao, CampoTexto, Faixa, LogoPersonalitec } from '@persona/ui';
import { Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useState, type FormEvent } from 'react';
import { mensagemDe } from '../src/api/erros';
import { useSessao } from '../src/sessao/sessao';

const EtapasSeguranca = lazy(() => import('./EtapasSeguranca').then((m) => ({ default: m.EtapasSeguranca })));

/** Só o usuário fica guardado neste navegador, nunca a senha. */
const CHAVE_USUARIO = 'persona.entrar.usuario';

function lerUsuarioLembrado(): string {
  try {
    return localStorage.getItem(CHAVE_USUARIO) ?? '';
  } catch {
    return '';
  }
}

function guardarUsuario(login: string | null) {
  try {
    if (login) localStorage.setItem(CHAVE_USUARIO, login);
    else localStorage.removeItem(CHAVE_USUARIO);
  } catch {
    // Navegador sem armazenamento (aba anônima, bloqueio): o login segue sem lembrar.
  }
}

/** Versão do servidor no rodapé, lida do /saude (público); sem resposta o rodapé fica só com a marca. */
function useVersaoServidor() {
  const [versao, setVersao] = useState<string | null>(null);
  useEffect(() => {
    const parar = new AbortController();
    fetch('/saude', { signal: parar.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((s: { versao?: unknown } | null) => { if (typeof s?.versao === 'string') setVersao(s.versao); })
      .catch(() => {});
    return () => parar.abort();
  }, []);
  return versao;
}

const FATOS = [
  { titulo: 'Nota fiscal eletrônica', texto: 'Emissão e consulta da NF-e dentro do faturamento.' },
  { titulo: 'Empresas e filiais', texto: 'Cada usuário trabalha na empresa e filial que escolher.' },
  { titulo: 'Acesso controlado', texto: 'Permissão por usuário e grupo em cada rotina.' },
] as const;

/** Lado do produto (decorativo): o sistema de verdade em tela e o que ele cobre. */
function Vitrine() {
  return (
    <section className="entrar-vitrine" aria-hidden="true">
      <p className="entrar-manchete">Toda a gestão da empresa em um só sistema</p>
      <p className="entrar-chamada">Cadastros, compras, estoque, vendas, fiscal e financeiro integrados, com as mesmas regras em todas as filiais.</p>
      <div className="entrar-tela" />
      <ul className="entrar-fatos">
        {FATOS.map((f) => <li key={f.titulo}><strong>{f.titulo}</strong>{f.texto}</li>)}
      </ul>
    </section>
  );
}

/** Login — Persona Experience (07 §0.1): o produto à esquerda, formulário à direita; abaixo de 1100px só o formulário. */
export function Entrar() {
  const { sessao, entrar, carregando, recarregar, sair } = useSessao();
  const { volta } = useSearch({ from: '/entrar' });
  const navegar = useNavigate();
  const versao = useVersaoServidor();
  const [lembrado] = useState(lerUsuarioLembrado);
  const [login, setLogin] = useState(lembrado);
  const [lembrar, setLembrar] = useState(lembrado !== '');
  const [senha, setSenha] = useState('');
  const [verSenha, setVerSenha] = useState(false);
  const [esqueci, setEsqueci] = useState(false);
  const [erro, setErro] = useState<{ texto: string; codigo?: string } | null>(null);
  const [etapa, setEtapa] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  if (!carregando && sessao) return <Navigate to={volta ?? '/'} />;
  const concluir = async () => {
    await recarregar();
    await navegar({ to: volta ?? '/' });
  };
  const cancelar = () => void sair().finally(() => { setEtapa(null); setSenha(''); });

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    // O botão fica sempre ativo (desabilitado ele parecia quebrado); o aviso diz o que falta.
    if (!login.trim() || !senha) {
      setErro({ texto: !login.trim() && !senha ? 'Informe o usuário e a senha.' : !login.trim() ? 'Informe o usuário.' : 'Informe a senha.' });
      return;
    }
    setErro(null);
    setOcupado(true);
    try {
      const s = await entrar(login.trim(), senha);
      guardarUsuario(lembrar ? login.trim() : null);
      if (s.etapa !== 'ok') setEtapa(s.etapa);
      else await navegar({ to: volta ?? '/' });
    } catch (x) {
      setErro(mensagemDe(x));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="entrar">
      <Vitrine />
      <main className="entrar-lado">
        <section className="entrar-caixa" aria-labelledby={etapa ? undefined : 'entrar-titulo'} aria-label={etapa ? 'Verificação de segurança' : undefined}>
          <div className="entrar-marca"><LogoPersonalitec tamanho={28} /> Persona <span>ERP</span></div>
          {etapa ? (
            <Suspense fallback={<div className="esqueleto" style={{ height: 240 }} aria-busy="true" />}>
              <EtapasSeguranca etapa={etapa} login={login.trim()} senhaDigitada={senha} aoMudarEtapa={setEtapa} aoConcluir={concluir} aoCancelar={cancelar} />
            </Suspense>
          ) : <>
            <h1 id="entrar-titulo" className="entrar-titulo">Acesse sua conta</h1>
            <p className="entrar-subtitulo">Entre com o usuário e a senha fornecidos pela sua empresa.</p>
            <form onSubmit={enviar} className="entrar-form" noValidate>
              <CampoTexto nome="login" rotulo="Usuário" valor={login} aoMudar={setLogin} autoFocus={!lembrado} autoComplete="username" />
              <CampoTexto nome="senha" rotulo="Senha" type={verSenha ? 'text' : 'password'} valor={senha} aoMudar={setSenha} autoFocus={!!lembrado}
                autoComplete="current-password"
                sufixo={<button type="button" className="entrar-mostrar" aria-pressed={verSenha} onClick={() => setVerSenha((v) => !v)}>{verSenha ? 'Ocultar' : 'Mostrar'}</button>} />
              <div className="entrar-opcoes">
                <label className="entrar-lembrar">
                  <input type="checkbox" checked={lembrar} onChange={(e) => setLembrar(e.target.checked)} /> Lembrar meu usuário
                </label>
                <button type="button" className="entrar-esqueci" aria-expanded={esqueci} onClick={() => setEsqueci((v) => !v)}>Esqueci minha senha</button>
              </div>
              {esqueci && <Faixa tipo="info">Peça ao administrador do sistema na sua empresa para gerar uma senha nova. No próximo acesso você escolhe a sua.</Faixa>}
              {erro && <Faixa tipo="erro" codigo={erro.codigo}>{erro.texto}</Faixa>}
              <Botao variante="primario" type="submit" carregando={ocupado} className="entrar-botao">Entrar</Botao>
            </form>
            <p className="entrar-aviso">A Personalitec nunca pede sua senha por telefone, e-mail ou mensagem.</p>
          </>}
        </section>
        <p className="entrar-rodape"><span>© {new Date().getFullYear()} Personalitec · Persona ERP</span>{versao && <span>Versão {versao}</span>}</p>
      </main>
    </div>
  );
}
